import type { SavedReply } from '~publish/gql/graphql'
import {
  type CreateSavedReply,
  useCreateSavedReply,
} from './useCreateSavedReply'
import {
  type DeleteSavedReply,
  useDeleteSavedReply,
} from './useDeleteSavedReply'
import {
  type UpdateSavedReply,
  useUpdateSavedReply,
} from './useUpdateSavedReply'

import { useSavedRepliesQuery } from './useSavedRepliesQuery'

type UseSavedRepliesReturn = {
  savedReplies: SavedReply[] | null
  loading: boolean
  error: string | null
  createSavedReply: CreateSavedReply
  deleteSavedReply: DeleteSavedReply
  updateSavedReply: UpdateSavedReply
}

/**
 * Hook to manage saved replies including fetching, creating, updating, and deleting saved replies.
 * @returns {UseSavedRepliesReturn} The saved replies, loading state, error state, and functions for CRUD operations on saved replies.
 * @example
 * const { savedReplies, loading, error, createSavedReply } = useSavedReplies()
 *
 * useEffect(() => {
 *   if (!loading && !error) {
 *     console.log(savedReplies);
 *   }
 * }, [savedReplies, loading, error])
 *
 * const handleCreateSavedReply = async () => {
 *   try {
 *     const newSavedReply = await createSavedReply({ title: 'New Saved Reply', content: 'This is a new saved reply' });
 *     console.log('Saved reply created with ID:', newSavedReply.id);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * }
 *
 * const handleUpdateSavedReply = async (savedReply) => {
 *   try {
 *     const updatedSavedReply = await updateSavedReply(savedReply);
 *     console.log('Saved reply updated:', updatedSavedReply);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * }
 *
 * const handleDeleteSavedReply = async (id) => {
 *   try {
 *     const deletedSavedReplyId = await deleteSavedReply(id);
 *     console.log('Saved reply deleted with ID:', deletedSavedReplyId);
 *   } catch (err) {
 *     console.error(err);
 *   }
 * }
 */
export function useSavedReplies(): UseSavedRepliesReturn {
  const { savedReplies, loading, error } = useSavedRepliesQuery()

  /*  @TODO: Adds limits for the saved replies */
  const createSavedReply = useCreateSavedReply()
  const deleteSavedReply = useDeleteSavedReply()
  const updateSavedReply = useUpdateSavedReply()

  return {
    savedReplies,
    loading,
    error,
    createSavedReply,
    deleteSavedReply,
    updateSavedReply,
  }
}
